// ═══════════════════════════════════════════════════
//  Shared upholstery design math
// ═══════════════════════════════════════════════════
//
// Channel, button, and spring counts for every upholstered banquette component.
// Neutral home for two reasons: back-cushion-cost.helpers.ts imports from
// banquette-cost.helpers.ts (so the base cannot import these from the back
// cushion), and these had four separate definitions across the client and
// server trees. Every former definition site now re-exports from here.

import { resolveDesignTraits, type UpholsteryDesignTraits } from './upholstery-design-traits';

/**
 * Channel count for one section.
 *
 * `runInches` is the dimension the design is laid out along: section width for
 * the base and both cushions, depth for an arm. `heightInches` is the cross
 * axis that horizontal channels divide.
 */
export function resolveChannelCount(
  design: any,
  runInches: number,
  heightInches: number,
  traits?: UpholsteryDesignTraits,
): number {
  const channelWidth = design?.channel_width ?? 0;
  if (channelWidth <= 0) return 0;

  // `traits` is optional so that call sites not yet threaded with catalog rows
  // keep working: resolveDesignTraits falls back to the seeded map, which
  // reproduces the string-literal comparisons this used to do.
  const t = traits ?? resolveDesignTraits(design?.type ?? 'plain');

  // The has_channels gate now runs BEFORE the override, which is a deliberate
  // change of order — see the note below this step. A design with no channels
  // has no channel count, whatever a stale override says.
  if (!t.has_channels) return 0;

  // Explicit null check, not a bare `> 0`: null and 0 both mean "no override".
  if (design?.channel_count_override != null && design.channel_count_override > 0) {
    return design.channel_count_override;
  }

  if (t.channel_axis === 'run') return Math.floor(runInches / channelWidth);
  if (t.channel_axis === 'cross') return Math.floor(heightInches / channelWidth);
  return 0;
}

/** Total button count. `runFeet` is linear FEET — an arm passes depth / 12. */
export function resolveButtonCount(
  design: any,
  runFeet: number,
  traits?: UpholsteryDesignTraits,
): number {
  const tuftingsPerFoot = design?.button_tuftings_per_foot ?? 0;
  if (tuftingsPerFoot <= 0 || runFeet <= 0) return 0;

  const t = traits ?? resolveDesignTraits(design?.type ?? 'plain');
  if (!t.has_buttons) return 0;
  return Math.ceil(runFeet * tuftingsPerFoot);
}

/** Total no-sag spring count across the item. Callers gate on springs_enabled. */
export function resolveSpringCount(
  springs: { spring_mode?: string; spring_spacing?: number; spring_count?: number },
  lfQuantity: number,
): number {
  const mode = springs.spring_mode ?? 'auto';
  if (mode === 'manual') return Math.max(0, Math.floor(springs.spring_count ?? 0));
  const spacing = springs.spring_spacing ?? 4;
  if (spacing <= 0) return 0;
  return Math.max(0, Math.floor((lfQuantity * 12) / spacing));
}
